import { Duration, LocalTime } from '@js-joda/core';
import { useMemo } from 'react';
import { useUpdateEffect } from 'react-use';

import { ValidationRules } from '@dreryk/react-common';
import { FormController } from '@dreryk/react-components';

import { personnelTranslationKeys } from '../../../../translations';
import { AvailabilityCreationFormBean } from '../../../availabilityCreation/availabilityCreation.types';
import { AvailabilityUpdateFormBean } from '../../../availabilityUpdate/availabilityUpdate.types';
import { AvailabilityFormController } from '../../common.types';

import { TimeSlotBean } from './AvailabilityTimeSlot.types';
import { TIME_SLOT_OTHER } from './AvailabilityTimeSlotFixed/AvailabilityTimeSlotFixed.types';
import { isManualTimeSlotInvalid } from './AvailabilityTimeSlotManual/AvailabilityTimeSlotManual.utils';

type AvailabilityTimeSlotValidatorFieldProps = {
   readonly register: AvailabilityFormController['register'];
   readonly trigger: AvailabilityFormController['trigger'];
   readonly watch: AvailabilityFormController['watch'];
};

const useValidationRules = (): ValidationRules<AvailabilityCreationFormBean | AvailabilityUpdateFormBean> => {
   return useMemo(
      () => ({
         validate: (_, getValue) => {
            const startTime = getValue('startTime') as LocalTime | null;
            const endTime = getValue('endTime') as LocalTime | null;

            if (!startTime || !endTime) {
               return undefined;
            }

            const availabilityDurationInMinutes = Duration.between(startTime, endTime).toMinutes();
            const slotDurationFixed = getValue('slotDurationFixed') as TimeSlotBean;
            const slotDurationManual = getValue('slotDurationManual') as number | null;

            if (slotDurationFixed === TIME_SLOT_OTHER) {
               if (!slotDurationManual) {
                  return personnelTranslationKeys.fillDurationOrChooseOtherOption;
               }

               if (isManualTimeSlotInvalid(slotDurationManual)) {
                  return personnelTranslationKeys.incorrectSlotDuration;
               }

               if (slotDurationManual > availabilityDurationInMinutes) {
                  return personnelTranslationKeys.availabilityShorterThanVisitTime;
               }
            }

            return undefined;
         },
      }),
      []
   );
};

export const AvailabilityTimeSlotValidatorField = (props: AvailabilityTimeSlotValidatorFieldProps): JSX.Element => {
   const slotDurationManual = props.watch('slotDurationManual');
   useUpdateEffect(() => {
      props.trigger('slotValidation');
   }, [slotDurationManual]);
   return <FormController controller={props.register('slotValidation', useValidationRules())} />;
};
